import React, { memo } from "react";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  LinearProgress,
} from "@mui/material";
import {
  Security as SecurityIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useState, useMemo } from "react";
import { useAuthMonitor } from "../../hooks/useAuthMonitor";
import logger from "../../helpers/logger";
import { GridCloseIcon } from "@mui/x-data-grid";

const AuthStatus = ({ showDetails = false, compact = false }) => {
  const {
    authStatus,
    isInitialized,
    refreshAuth,
    checkStatus,
    isHealthy,
    isWarning,
    isCritical,
    isExpired,
    requiresAction,
  } = useAuthMonitor();

  const [showDialog, setShowDialog] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get status color and icon
  const getStatusConfig = () => {
    if (!isInitialized) {
      return {
        color: "default",
        icon: <SecurityIcon />,
        label: "Initializing...",
        severity: "info",
      };
    }

    switch (authStatus.status) {
      case "healthy":
        return {
          color: "success",
          icon: <CheckCircleIcon />,
          label: "", // No label for healthy state - just the green icon
          severity: "success",
        };
      case "warning":
        return {
          color: "warning",
          icon: <WarningIcon />,
          label: "Session Expiring",
          severity: "warning",
        };
      case "critical":
        return {
          color: "error",
          icon: <ErrorIcon />,
          label: "Session Critical",
          severity: "error",
        };
      case "expired":
        return {
          color: "error",
          icon: <ErrorIcon />,
          label: "Session Expired",
          severity: "error",
        };
      case "renewed":
        return {
          color: "success",
          icon: <CheckCircleIcon />,
          label: "Session Renewed",
          severity: "success",
        };
      default:
        return {
          color: "default",
          icon: <InfoIcon />,
          label: "Unknown Status",
          severity: "info",
        };
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    logger.log("🔄 User requested authentication refresh");

    try {
      const success = await refreshAuth();
      if (success) {
        logger.log("✅ Authentication refresh successful");
      } else {
        logger.warn("⚠️ Authentication refresh failed");
      }
    } catch (error) {
      logger.error("❌ Authentication refresh error", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCheckStatus = async () => {
    logger.log("🔍 User requested status check");
    await checkStatus();
  };

  const statusConfig = useMemo(
    () => getStatusConfig(),
    [authStatus.status, isInitialized]
  );

  // Compact mode - just show a small indicator
  if (compact) {
    // For healthy status, show just an icon button (minimal UI)
    if (authStatus.status === "healthy") {
      return (
        <Tooltip
          title={`Session active${authStatus.expiresIn
            ? ` (${Math.round(authStatus.expiresIn)} min remaining)`
            : ""
            }`}
        >
          <IconButton
            size="small"
            onClick={() => setShowDialog(true)}
            sx={{
              color: "white",
              "&:hover": {
                opacity: 0.9,
              },
            }}
          >
            <CheckCircleIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Tooltip>
      );
    }

    // For warning/critical states, show chip with text
    return (
      <Tooltip
        title={`Authentication: ${authStatus.message || statusConfig.label}`}
      >
        <Chip
          icon={statusConfig.icon}
          label={statusConfig.label}
          color={statusConfig.color}
          size="small"
          onClick={() => setShowDialog(true)}
          sx={{
            cursor: "pointer",
            "& .MuiChip-icon": {
              fontSize: "0.9rem",
            },
          }}
        />
      </Tooltip>
    );
  }

  // Full display mode
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          borderRadius: 1,
          backgroundColor:
            statusConfig.color === "error"
              ? "error.light"
              : statusConfig.color === "warning"
                ? "warning.light"
                : statusConfig.color === "success"
                  ? "success.light"
                  : "grey.100",
          border: 1,
          borderColor:
            statusConfig.color === "error"
              ? "error.main"
              : statusConfig.color === "warning"
                ? "warning.main"
                : statusConfig.color === "success"
                  ? "success.main"
                  : "grey.300",
        }}
      >
        {statusConfig.icon}

        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {statusConfig.label}
          </Typography>
          {authStatus.message && (
            <Typography variant="caption" color="text.secondary">
              {authStatus.message}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Refresh authentication">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              color={statusConfig.color}
            >
              <RefreshIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>

          {showDetails && (
            <Tooltip title="View details">
              <IconButton
                size="small"
                onClick={() => setShowDialog(true)}
                color={statusConfig.color}
              >
                <InfoIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Details Dialog */}
      <Dialog
        open={showDialog}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          setShowDialog(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {statusConfig.icon}
            Authentication Status
          </Box>
          <IconButton
            onClick={() => { setShowDialog(false) }}
            style={{ position: 'absolute', right: 10, top: 12 }}
            aria-label="close"
            title="Close"
          >
            <GridCloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Alert severity={statusConfig.severity} sx={{ mb: 2 }}>
            {authStatus.message || statusConfig.label}
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Status Details:
            </Typography>
            <Typography variant="body2">
              • Status: <strong>{authStatus.status}</strong>
            </Typography>
            {authStatus.expiresIn && (
              <Typography variant="body2">
                • Time Remaining:{" "}
                <strong>{Math.round(authStatus.expiresIn)} minutes</strong>
              </Typography>
            )}
            <Typography variant="body2">
              • Requires Action:{" "}
              <strong>{requiresAction ? "Yes" : "No"}</strong>
            </Typography>
            <Typography variant="body2">
              • Monitor Active: <strong>{isInitialized ? "Yes" : "No"}</strong>
            </Typography>
          </Box>

          {requiresAction && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Your session requires attention. Consider refreshing your
                authentication or saving your work.
              </Typography>
            </Alert>
          )}

          {isRefreshing && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Refreshing authentication...
              </Typography>
              <LinearProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCheckStatus} disabled={isRefreshing}>
            Check Status
          </Button>
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
            variant="outlined"
          >
            Refresh Auth
          </Button>
          <Button onClick={() => setShowDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default memo(AuthStatus);
